import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import request from '@teetsh/app/src/utils/request';
import { School } from '@teetsh/app/src/types/school';
import { deserializeSchoolyear } from '@teetsh/app/src/queries/schoolyears/serializer';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';

export const LAST_SCHOOLYEAR_SCHOOL_KEY = 'lastSchoolyearSchool';

interface Props {
  schoolyearId?: string;
  schoolId?: string;
}

function getLastSchool(tz: string) {
  return request('/school', {
    method: 'GET',
  }).then(res => {
    const { school } = res;
    return {
      schoolyear: deserializeSchoolyear(res.schoolyear, tz),
      school: school as School,
    };
  });
}

function getSchoolSchoolyear(
  schoolyearId: string,
  schoolId: string,
  tz: string,
) {
  return request(
    `/schoolyears/${schoolyearId}/schools/${schoolId}?include=schoolyear`,
    { method: 'GET' },
  ).then(res => {
    const { school } = res;
    return {
      schoolyear: deserializeSchoolyear(res.schoolyear, tz),
      school: school as School,
    };
  });
}

export default function useLastSchoolyearSchool({
  schoolyearId,
  schoolId,
}: Props) {
  const user = useUser();
  return useQuery({
    queryKey: [LAST_SCHOOLYEAR_SCHOOL_KEY, schoolyearId, schoolId],
    // Il faudra probablement à l'avenir séparer ce hook en 2
    // le lastSchool sert uniquement à faire le redirect
    // donc on a pas besoin de le garder du tout en cash
    queryFn: () => {
      if (schoolyearId && schoolId) {
        return getSchoolSchoolyear(schoolyearId, schoolId, user.location);
      }
      return getLastSchool(user.location);
    },
    // S'il n'y a pas de schoolyearId, on ne veut pas garder les données
    // car ça sert seulement pour le redirect
    gcTime: !schoolyearId ? 0 : 5 * (60 * 1000),
    retry: (failCount, err) => {
      const status = (err as AxiosError)?.response?.status;
      // Si on a une 400 c'est pas la peine de retry
      // Va savoir pourquoi mais lorsqu'on retry au bout d'un moment
      // On a isLoading a false, alors qu'on est toujours en train de retry
      // Et donc data est undefined et ça pose ensuite des problèmes
      // de context provider school schoolyear
      const is4xx = status ? status >= 400 && status < 500 : false;
      if (is4xx) {
        return false;
      }
      return failCount < 3;
    },

    refetchOnWindowFocus: false,
    placeholderData: previousData =>
      !schoolyearId || !schoolId ? previousData : undefined,
  });
}
